import React, { useEffect, useState } from 'react';
import { 
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Avatar,
  Box, 
  Paper,
  MenuItem,
  Select
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Cookies from 'js-cookie';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import AdminRank from '../components/AdminRank.tsx';

// DataGrid import az @mui/x-data-grid-ből
import { DataGrid, GridColDef } from '@mui/x-data-grid';

// Segédfüggvény az adatok titkosításához
const encryptData = (data: string) => btoa(data);

interface AdminAvatar {
  id: number;
  url: string;
  price: number;
  rank?: string | null;
}

interface User {
  id: number;
  username: string;
  email: string;
  score: number;
}

interface Product {
  id: number;
  productName: string;
  productType: { id: number; typeName: string };
  price: number;
  hidden: boolean;
}

const AdminPanel = () => {
  // --------------------- USER MANAGEMENT ---------------------
  const [users, setUsers] = useState<User[]>([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openScoreEdit, setOpenScoreEdit] = useState(false);
  const [openTokenEdit, setOpenTokenEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editData, setEditData] = useState<{ username: string; email: string }>({ username: '', email: '' });
  const [scoreData, setScoreData] = useState<number>(0);
  const [tokenData, setTokenData] = useState<string>('');
  const [userTokens, setUserTokens] = useState<Array<{ id: number; token: string }>>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: ''
  });
  const [openAvatarEdit, setOpenAvatarEdit] = useState(false);
  const [allAvatars, setAllAvatars] = useState<Array<{ avatarId: number; url: string }>>([]);
  const [userAvatarIds, setUserAvatarIds] = useState<number[]>([]);

  // ---------------------Avatar Data Management---------------------
  const [adminAvatars, setAdminAvatars] = useState<AdminAvatar[]>([]);
  const [openCreateAvatarDialog, setOpenCreateAvatarDialog] = useState(false);
  const [openEditAvatarDialog, setOpenEditAvatarDialog] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<AdminAvatar | null>(null);
  const [createAvatarFile, setCreateAvatarFile] = useState<File | null>(null);
  const [createAvatarPrice, setCreateAvatarPrice] = useState('');
  const [editAvatarFile, setEditAvatarFile] = useState<File | null>(null);
  const [editAvatarPrice, setEditAvatarPrice] = useState('');
  const [createAvatarRank, setCreateAvatarRank] = useState('');
  const [editAvatarRank, setEditAvatarRank] = useState('');
  const [availableRanks, setAvailableRanks] = useState<string[]>([]);
  const [createAvatarDescription, setCreateAvatarDescription] = useState('');
  const [editAvatarDescription, setEditAvatarDescription] = useState('');

  // Fetch available ranks (az API visszaadja például: ["AdamantineParagon", "OnyxOverlord", ...])
  useEffect(() => {
    const fetchAvailableRanks = async () => {
      try {
        const token = Cookies.get('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/getallranks`, {
          headers: { 
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${token}` 
          }
        });
        const data = await response.json();
        // Feltételezzük, hogy data egy tömb, pl.:
        // [
        //   { "price": 10.00, "name": "CopperApprentice" },
        //   { "price": 50.00, "name": "ObsidianArtificer" }
        // ]
        const names = Array.isArray(data) ? data.map((item: any) => item.name) : [];
        setAvailableRanks(names);
      } catch (error) {
        console.error('Error fetching available ranks:', error);
      }
    };
    fetchAvailableRanks();
  }, []);
  

  // Fetch avatarok összevonása az admin és user API-kból
  const fetchAvatars = async () => {
    try {
      const token = Cookies.get('token');
      const resAdmin = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/getallavatars`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const resUser = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/getallavatars`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const dataAdmin = await resAdmin.json();
      const dataUser = await resUser.json();
      
      const adminArr = dataAdmin.avatars || dataAdmin;
      const userArr = dataUser.avatars || dataUser;
      
      // Összekapcsolás URL alapján, valamint a rank értéket is átvesszük
      const merged = adminArr.map((avatar: any) => {
        const matching = userArr.find((a: any) => a.url === avatar.url);
        return {
          id: avatar.avatarId || avatar.id,
          url: avatar.url,
          price: matching ? matching.price : 0,
          rank: matching ? matching.rank : null
        };
      });
      setAdminAvatars(merged);
    } catch (error) {
      console.error('Error fetching avatars:', error);
    }
  };

  useEffect(() => {
    fetchAvatars();
  }, []);
  

  useEffect(() => {
    fetchAvatars();
  }, []);

  // File változások kezelése a Create és Edit Dialogokban
  const handleCreateAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCreateAvatarFile(e.target.files[0]);
    }
  };

  const handleEditAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setEditAvatarFile(e.target.files[0]);
    }
  };

  // Create Avatar Dialog mentése
  // --- Create Avatar ---
const handleCreateAvatarSubmit = async () => {
  const token = Cookies.get('token');
  if (!createAvatarFile || !createAvatarPrice) {
    showNotification('error', 'Please provide an image and price.');
    return;
  }

  const formData = new FormData();
  formData.append("file", createAvatarFile);
  formData.append("price", createAvatarPrice);
  formData.append("rank", createAvatarRank);
  formData.append("description", createAvatarDescription);  // Új mező

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/createavatar`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    const data = await response.json();
    if (response.ok) {
      showNotification('success', 'Avatar created successfully!');
      setOpenCreateAvatarDialog(false);
      setCreateAvatarFile(null);
      setCreateAvatarPrice('');
      setCreateAvatarRank('');
      setCreateAvatarDescription('');
      fetchAvatars();
    } else {
      showNotification('error', data.error || 'Creation failed');
    }
  } catch (error) {
    showNotification('error', 'An error occurred during creation.');
  }
};

  // Edit Avatar Dialog mentése – ha új fájl lett feltöltve, akkor azt base64-re konvertáljuk
  // Edit Avatar Dialog mentése
  // --- Edit Avatar ---
const handleEditAvatarSubmit = async () => {
  if (!selectedAvatar) return;
  const token = Cookies.get('token');

  let newUrl = selectedAvatar.url;
  if (editAvatarFile) {
    newUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(editAvatarFile);
    });
  }

  const newPrice = editAvatarPrice || selectedAvatar.price.toString();

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/editavatardatas/${selectedAvatar.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        url: newUrl,
        price: newPrice,
        rank: editAvatarRank,
        description: editAvatarDescription || (selectedAvatar?.description ?? '')
      })
    });

    const data = await response.json();
    if (response.ok) {
      showNotification('success', 'Avatar updated successfully!');
      setOpenEditAvatarDialog(false);
      setSelectedAvatar(null);
      setEditAvatarFile(null);
      setEditAvatarPrice('');
      setEditAvatarRank('');
      setEditAvatarDescription('');
      fetchAvatars();
    } else {
      showNotification('error', data.error || 'Update failed');
    }
  } catch (error) {
    showNotification('error', 'An error occurred during update.');
  }
};


  // --------------------- PRODUCT MANAGEMENT ---------------------
  const [products, setProducts] = useState<Product[]>([]);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [productDialogMode, setProductDialogMode] = useState<"create" | "edit">("create");
  const [productFormData, setProductFormData] = useState<{ productName: string; productTypeId: number; price: number }>({
        productName: '',
        productTypeId: 0,
        price: 0
      });
  const [productTypes, setProductTypes] = useState<Array<{ id: number; typeName: string }>>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [openProductTypeDialog, setOpenProductTypeDialog] = useState(false);
  const [productTypeFormData, setProductTypeFormData] = useState<{ typeName: string }>({ typeName: '' });

  useEffect(() => {
      if (productTypes.length > 0 && productFormData.productTypeId === 0) {
        setProductFormData(prev => ({ ...prev, productTypeId: productTypes[0].id }));
      }
    }, [productTypes, productFormData.productTypeId]);

// Fetch product types from API
  const fetchProductTypes = async () => {
      try {
        const token = Cookies.get('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/getallproducttype`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        setProductTypes(data);
      } catch (error) {
        console.error('Error fetching product types:', error);
      }
    };
  
    useEffect(() => {
      fetchProductTypes();
    }, []);


  useEffect(() => {
    fetchUsers();
    fetchTokens();
    fetchProducts();
  }, []);

  // USER MANAGEMENT API-k
  const fetchUsers = async () => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []); // <- Biztosítsd, hogy tömb legyen
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]); // <- Üres tömb hiba esetén
    }
  };

  const fetchTokens = async () => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/getalltokens`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setUserTokens(data);
    } catch (error) {
      console.error('Error fetching tokens:', error);
    }
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setEditData({ username: user.username, email: user.email });
    setOpenEdit(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setOpenDelete(true);
  };

  const handleTokenEditClick = (user: User) => {
    setSelectedUser(user);
    const found = userTokens.find(item => item.id === user.id);
    setTokenData(found ? found.token : '');
    setOpenTokenEdit(true);
  };

  const showNotification = (severity: 'success' | 'error', message: string) => {
    setSnackbar({ open: true, severity, message });
  };

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/update/${selectedUser?.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editData)
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('username', encryptData(editData.username));
        localStorage.setItem('email', encryptData(editData.email));
        showNotification('success', 'User updated successfully!');
        setOpenEdit(false);
        fetchUsers();
      } else {
        showNotification('error', data.message || 'Update failed');
      }
    } catch (error) {
      showNotification('error', 'An error occurred');
    }
  };

  const handleScoreEditClick = (user: User) => {
    setSelectedUser(user);
    setScoreData(user.score);
    setOpenScoreEdit(true);
  };

  const handleScoreUpdate = async () => {
    if (selectedUser) {
      try {
        const token = Cookies.get('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/updateScore/${selectedUser.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(scoreData)
        });
        const data = await response.json();
        if (response.ok) {
          showNotification('success', 'Score updated successfully!');
          setOpenScoreEdit(false);
          fetchUsers();
        } else {
          showNotification('error', data.message || 'Score update failed');
        }
      } catch (error) {
        showNotification('error', 'An error occurred while updating the score');
      }
    }
  };

  const handleTokenUpdate = async () => {
    if (selectedUser) {
      try {
        const token = Cookies.get('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/updateToken/${selectedUser.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ token: tokenData })
        });
        const data = await response.json();
        if (response.ok) {
          showNotification('success', 'Token updated successfully!');
          setOpenTokenEdit(false);
          fetchTokens();
        } else {
          showNotification('error', data.message || 'Token update failed');
        }
      } catch (error) {
        showNotification('error', 'An error occurred while updating token');
      }
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/delete/${selectedUser?.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      showNotification(response.ok ? 'success' : 'error', data.message || 'Deletion failed');
      if (response.ok) {
        fetchUsers();
        setOpenDelete(false);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      showNotification('error', 'An error occurred');
    }
  };

  const banUser = async (user: User) => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/ban/${user.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      showNotification(response.ok ? 'success' : 'error', data.message || 'Ban failed');
      if (response.ok) fetchUsers();
    } catch (error) {
      console.error('Error banning user:', error);
      showNotification('error', 'An error occurred');
    }
  };

  const unbanUser = async (user: User) => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/unban/${user.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      showNotification(response.ok ? 'success' : 'error', data.message || 'Unban failed');
      if (response.ok) fetchUsers();
    } catch (error) {
      console.error('Error unbanning user:', error);
      showNotification('error', 'An error occurred');
    }
  };

  const handleEditAvatars = async (user: User) => {
    setSelectedUser(user);
    const token = Cookies.get('token');
    try {
      const resAll = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/getallavatars`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const dataAll = await resAll.json();
      setAllAvatars(dataAll.avatars || []);
      const resUser = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/getavatars/${user.id}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const dataUser = await resUser.json();
      const avatarIds = dataUser.userAvatars.map((ua: any) => ua.avatarId);
      setUserAvatarIds(avatarIds);
      setOpenAvatarEdit(true);
    } catch (error) {
      console.error('Error fetching avatars:', error);
    }
  };

  const handleAvatarUpdate = async () => {
    const token = Cookies.get('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/updateavatars/${selectedUser?.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ avatarIds: userAvatarIds })
      });
      const data = await response.json();
      if (response.ok) {
        showNotification('success', 'User avatars updated successfully!');
        setOpenAvatarEdit(false);
        fetchUsers();
      } else {
        showNotification('error', data.message || 'Avatar update failed');
      }
    } catch (error) {
      console.error('Error updating avatars:', error);
      showNotification('error', 'An error occurred while updating avatars');
    }
  };

  // DataGrid oszlopok a User Management részhez
  const userColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'username', headerName: 'Username', width: 200 },
    { field: 'email', headerName: 'Email', width: 300 },
    { field: 'score', headerName: 'Score', width: 150 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 750,
      renderCell: (params) => (
        <>
         <Button onClick={() => handleEditAvatars(params.row)} startIcon={<EditIcon />} sx={{ mr: 1 }}>
           Edit Avatars
         </Button>
          <Button onClick={() => handleEditClick(params.row)} startIcon={<EditIcon />} sx={{ mr: 1 }}>
            Edit
          </Button>
          <Button onClick={() => handleDeleteClick(params.row)} startIcon={<DeleteIcon />} color="error" sx={{ mr: 1 }}>
            Delete
          </Button>
          {params.row.banned ? (
            <Button onClick={() => unbanUser(params.row)} startIcon={<DoNotDisturbAltIcon />} color="success" sx={{ mr: 1 }}>
              Unban
            </Button>
          ) : (
            <Button onClick={() => banUser(params.row)} startIcon={<DoNotDisturbAltIcon />} color="warning" sx={{ mr: 1 }}>
              Ban
            </Button>
          )}
          <Button onClick={() => handleScoreEditClick(params.row)} startIcon={<EditIcon />} sx={{ mr: 1 }}>
            Score Edit
          </Button>
          <Button onClick={() => handleTokenEditClick(params.row)} startIcon={<EditIcon />} sx={{ mr: 1 }}>
            Edit Token
          </Button>
        </>
      )
    }
  ];

  // DataGrid oszlopok a Product Management részhez
  const productColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'image',
      headerName: 'Image',
      width: 80,
      renderCell: (params) => {
        if (params.row.image && params.row.image.startsWith('data:image')) {
          return <img src={params.row.image} alt="product" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />;
        }
        return <Typography variant="caption" color="textSecondary">No Image</Typography>;
      }
    },
    { field: 'productName', headerName: 'Product Name', width: 200 },
    { field: 'price', headerName: 'Price', width: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 600,
      renderCell: (params) => (
        <>
          <Button 
            onClick={() => {
              setProductDialogMode("edit");
              setSelectedProduct(params.row);
              setProductFormData({
                productName: params.row.productName,
                productTypeId: params.row.productType.id,
                price: params.row.price,
                // --- JAVÍTÁS: A meglévő képet is be kell tölteni a state-be! ---
                imageBase64: params.row.image || '' 
              });
              setOpenProductDialog(true);
            }} 
            startIcon={<EditIcon />} 
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button 
            onClick={() => handleProductDelete(params.row)} 
            startIcon={<DeleteIcon />} 
            color="error" 
            sx={{ mr: 1 }}
          >
            Delete
          </Button>
          {!params.row.active ? (
            <Button onClick={() => handleProductUnhide(params.row)} color="success" sx={{ mr: 1 }}>
              Unhide
            </Button>
          ) : (
            <Button onClick={() => handleProductHide(params.row)} color="warning" sx={{ mr: 1 }}>
              Hide
            </Button>
          )}
        </>
      )
    }
  ];

  // Product API függvények
  const fetchProducts = async () => {
  try {
    const token = Cookies.get('token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/getallproduct`, {
            headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });
    const data = await response.json();
    setProducts(Array.isArray(data) ? data : []); // <- Biztosítsd, hogy tömb legyen
  } catch (error) {
    console.error('Error fetching products:', error);
    setProducts([]); // <- Üres tömb hiba esetén
  }
};

  const handleProductSave = async () => {
    try {
      const token = Cookies.get('token');
      if (productDialogMode === "create") {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/createproduct`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(productFormData)
        });
        const data = await response.json();
        if (response.ok) {
          showNotification('success', 'Product created successfully!');
          setOpenProductDialog(false);
          fetchProducts();
        } else {
          showNotification('error', data.message || 'Product creation failed');
        }
      } else {
        if (selectedProduct) {
          // Az update API hívása a termék id-jával az URL-ben
          const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/updateproduct/${selectedProduct.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(productFormData)
          });
          const data = await response.json();
          if (response.ok) {
            showNotification('success', 'Product updated successfully!');
            setOpenProductDialog(false);
            fetchProducts();
          } else {
            showNotification('error', data.message || 'Product update failed');
          }
        }
      }
    } catch (error) {
      showNotification('error', 'An error occurred while saving the product');
    }
  };

  const handleProductDelete = async (product: Product) => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/deleteproduct/${product.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        showNotification('success', 'Product deleted successfully!');
        fetchProducts();
      } else {
        showNotification('error', data.message || 'Product deletion failed');
      }
    } catch (error) {
      showNotification('error', 'An error occurred while deleting the product');
    }
  };
  
  // Hide Product – az adott termék id-ját illesztjük az URL-be
  const handleProductHide = async (product: Product) => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/hideproduct/${product.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        showNotification('success', 'Product hidden successfully!');
        fetchProducts();
      } else {
        showNotification('error', data.message || 'Product hide failed');
      }
    } catch (error) {
      showNotification('error', 'An error occurred while hiding the product');
    }
  };
  
  // Unhide Product – az adott termék id-ját illesztjük az URL-be
  const handleProductUnhide = async (product: Product) => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/unhideproduct/${product.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        showNotification('success', 'Product unhidden successfully!');
        fetchProducts();
      } else {
        showNotification('error', data.message || 'Product unhide failed');
      }
    } catch (error) {
      showNotification('error', 'An error occurred while unhiding the product');
    }
  };

  const handleProductTypeSave = async () => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/createproducttype`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ typeName: productTypeFormData.typeName })
      });
      const data = await response.json();
      if (response.ok) {
        showNotification('success', 'Product type created successfully!');
        setOpenProductTypeDialog(false);
        fetchProducts();
      } else {
        showNotification('error', data.message || 'Product type creation failed');
      }
    } catch (error) {
      showNotification('error', 'An error occurred while creating product type');
    }
  };

  // --------------------- RETURN JSX ---------------------
  return (
    <Paper sx={{ padding: 3, borderRadius: 2, boxShadow: 3, mt: '64px', }}>
      {/* USER MANAGEMENT Section */}
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', color: 'primary.main' }}>
        User Management
      </Typography>
      <DataGrid
        rows={users}
        columns={userColumns}
        pageSize={5}
        rowsPerPageOptions={[5, 10, 20]}
        pagination
        autoHeight
        disableSelectionOnClick
        rowCount={users.length} // <- Adj hozzá ezt
        sx={{ border: 'none', boxShadow: 1, mb: 4 }}
      />

      {/* Dialog-ok a User Management részhez */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Username"
            fullWidth
            value={editData.username}
            onChange={(e) => setEditData({ ...editData, username: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email"
            fullWidth
            value={editData.email}
            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} color="secondary">Cancel</Button>
          <Button onClick={handleUpdateSubmit} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openScoreEdit} onClose={() => setOpenScoreEdit(false)}>
        <DialogTitle>Edit Score</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Score"
            fullWidth
            type="number"
            value={scoreData}
            onChange={(e) => setScoreData(Number(e.target.value))}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenScoreEdit(false)} color="secondary">Cancel</Button>
          <Button onClick={handleScoreUpdate} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openTokenEdit} onClose={() => setOpenTokenEdit(false)}>
        <DialogTitle>Edit Token</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Token"
            fullWidth
            value={tokenData}
            onChange={(e) => setTokenData(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTokenEdit(false)} color="secondary">Cancel</Button>
          <Button onClick={handleTokenUpdate} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAvatarEdit} onClose={() => setOpenAvatarEdit(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Avatars for {selectedUser?.username}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            {allAvatars.map((avatar) => {
              const isSelected = userAvatarIds.includes(avatar.avatarId);
              return (
                <Box key={avatar.avatarId} sx={{ position: 'relative' }}>
                  <Avatar
                    src={avatar.url}
                    sx={{
                      width: 80,
                      height: 80,
                      border: isSelected ? '3px solid green' : '1px solid grey',
                      opacity: isSelected ? 1 : 0.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setUserAvatarIds((prev) =>
                        prev.includes(avatar.avatarId)
                          ? prev.filter((id) => id !== avatar.avatarId)
                          : [...prev, avatar.avatarId]
                      );
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAvatarEdit(false)} color="secondary">Cancel</Button>
          <Button onClick={handleAvatarUpdate} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Rank Management
        </Typography>
        <AdminRank 
          showNotification={showNotification} 
        />
      </Box>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete user {selectedUser?.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} color="secondary">Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* --------------------- Avatar Data Management Section --------------------- */}

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Avatar Data Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button variant="contained" color="primary" onClick={() => setOpenCreateAvatarDialog(true)}>
            Create Avatar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (selectedAvatar) {
                // Állítsuk be az Edit dialog alapértékeit
                setEditAvatarPrice(selectedAvatar.price.toString());
                setEditAvatarRank(selectedAvatar.rank || '');
                setOpenEditAvatarDialog(true);
              } else {
                showNotification('error', 'Please select an avatar to edit.');
              }
            }}
          >
            Edit Avatar Data
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {adminAvatars.map((avatar) => (
            <Box
              key={avatar.id}
              sx={{
                border: selectedAvatar?.id === avatar.id ? '2px solid blue' : '1px solid grey',
                padding: 1,
                cursor: 'pointer'
              }}
              onClick={() => setSelectedAvatar(avatar)}
            >
              <Avatar src={avatar.url} sx={{ width: 80, height: 80 }} />
              <Typography variant="caption">ID: {avatar.id}</Typography>
              <Typography variant="caption">Price: {avatar.price}</Typography>
              <Typography variant="caption">Rank: {avatar.rank || 'NULL'}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Create Avatar Dialog */}
      <Dialog open={openCreateAvatarDialog} onClose={() => setOpenCreateAvatarDialog(false)}>
        <DialogTitle>Create Avatar</DialogTitle>
        <DialogContent>
          <Button variant="contained" component="label">
            Upload Image
            <input type="file" hidden accept="image/png, image/jpeg" onChange={handleCreateAvatarFileChange} />
          </Button>
          {createAvatarFile && <Typography variant="body2" sx={{ mt: 1 }}>{createAvatarFile.name}</Typography>}
          <TextField
            margin="dense"
            label="Price"
            fullWidth
            value={createAvatarPrice}
            onChange={(e) => setCreateAvatarPrice(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Select Rank</Typography>
          <Select
            fullWidth
            value={createAvatarRank}
            onChange={(e) => setCreateAvatarRank(e.target.value)}
            margin="dense"
          >
            <MenuItem value="">NULL</MenuItem>
            {availableRanks.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateAvatarDialog(false)} color="secondary">Cancel</Button>
          <Button onClick={handleCreateAvatarSubmit} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Avatar Data Dialog */}
      <Dialog open={openEditAvatarDialog} onClose={() => setOpenEditAvatarDialog(false)}>
        <DialogTitle>Edit Avatar Data</DialogTitle>
        <DialogContent>
          <Button variant="contained" component="label">
            Upload New Image (Optional)
            <input type="file" hidden accept="image/png, image/jpeg" onChange={handleEditAvatarFileChange} />
          </Button>
          {editAvatarFile ? (
            <Typography variant="body2" sx={{ mt: 1 }}>{editAvatarFile.name}</Typography>
          ) : (
            selectedAvatar && <Avatar src={selectedAvatar.url} sx={{ width: 80, height: 80, mt: 1 }} />
          )}

          <TextField
            margin="dense"
            label="Price"
            fullWidth
            value={editAvatarPrice || (selectedAvatar ? selectedAvatar.price : '')}
            onChange={(e) => setEditAvatarPrice(e.target.value)}
            sx={{ mt: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mt: 2 }}>Select Rank</Typography>
          <Select
            fullWidth
            value={editAvatarRank}
            onChange={(e) => setEditAvatarRank(e.target.value)}
            margin="dense"
          >
            <MenuItem value="">NULL</MenuItem>
            {availableRanks.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </Select>

          <TextField
            margin="dense"
            label="Description"
            fullWidth
            value={editAvatarDescription || (selectedAvatar?.description ?? '')}
            onChange={(e) => setEditAvatarDescription(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditAvatarDialog(false)} color="secondary">Cancel</Button>
          <Button onClick={handleEditAvatarSubmit} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* --------------------- PRODUCT MANAGEMENT Section --------------------- */}
      <Paper sx={{ padding: 3, mt: 4, borderRadius: 2, boxShadow: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" color="primary">
            Product Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => {
                setProductDialogMode("create");
                // ÚJ: imageBase64 hozzáadása az alapállapothoz
                setProductFormData({ productName: '', productTypeId: 0, price: 0, imageBase64: '' });
                setSelectedProduct(null);
                setOpenProductDialog(true);
              }}
            >
              Create Product
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenProductTypeDialog(true)}
            >
              Create Product Type
            </Button>
          </Box>
        </Box>
        <DataGrid
          rows={products}
          columns={productColumns}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          pagination
          autoHeight
          disableSelectionOnClick
          rowCount={products.length}
          sx={{ border: 'none', boxShadow: 1 }}
          />
      </Paper>

      {/* Dialog for Create/Edit Product */}
      <Dialog open={openProductDialog} onClose={() => setOpenProductDialog(false)}>
        <DialogTitle>{productDialogMode === "create" ? "Create Product" : "Edit Product"}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Product Name"
            fullWidth
            value={productFormData.productName}
            onChange={(e) => setProductFormData({ ...productFormData, productName: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            select
            margin="dense"
            label="Product Type"
            fullWidth
            value={productFormData.productTypeId || ''}
            onChange={(e) => setProductFormData({ ...productFormData, productTypeId: Number(e.target.value) })}
            SelectProps={{ native: false }}
            sx={{ mb: 2 }}
          >
            {productTypes.map((pt) => (
              <MenuItem key={pt.id} value={pt.id}>
                {pt.typeName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Price"
            fullWidth
            type="number"
            value={productFormData.price}
            onChange={(e) => setProductFormData({ ...productFormData, price: Number(e.target.value) })}
            sx={{ mb: 3 }}
          />

          {/* --- DRAG AND DROP KÉPFELTÖLTŐ (10MB LIMIT) --- */}
          <Box
            sx={{
              border: '2px dashed grey',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: 'rgba(0,0,0,0.02)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files?.[0];
              if (file) {
                // Szigorú PNG ellenőrzés
                if (file.type !== 'image/png') {
                  alert('Error: Please drop a valid PNG image format only.');
                  return;
                }
                // 10MB fájlméret limit ellenőrzése
                if (file.size > 10 * 1024 * 1024) {
                  alert('Error: The image size must be less than 10MB.');
                  return;
                }
                
                const reader = new FileReader();
                reader.onloadend = () => {
                  setProductFormData({ ...productFormData, imageBase64: reader.result as string });
                };
                reader.readAsDataURL(file);
              }
            }}
          >
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
              Drag & Drop a PNG image here
            </Typography>
            
            {/* --- ÚJ RÉSZ: Max 10MB figyelmeztetés --- */}
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'error.main' }}>
              Max file size: 10MB
            </Typography>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>or</Typography>
            
            <Button variant="outlined" component="label" size="small">
              Browse Files (PNG ONLY)
              <input 
                type="file" 
                hidden 
                accept="image/png" 
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // Szigorú PNG ellenőrzés
                    if (file.type !== 'image/png') {
                      alert('Error: Please upload a valid PNG image format only.');
                      return;
                    }
                    // 10MB fájlméret limit ellenőrzése
                    if (file.size > 10 * 1024 * 1024) {
                      alert('Error: The image size must be less than 10MB.');
                      // Reseteljük az inputot, hogy újra ki lehessen választani ugyanazt a fájlt hiba után is
                      e.target.value = ''; 
                      return;
                    }

                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setProductFormData({ ...productFormData, imageBase64: reader.result as string });
                    };
                    reader.readAsDataURL(file);
                  }
                }} 
              />
            </Button>
          </Box>

          {/* Kép előnézete */}
          {productFormData.imageBase64 && (
            <Box sx={{ mt: 2, textAlign: 'center', border: '1px solid lightgrey', p: 1, borderRadius: 2 }}>
              <Typography variant="caption" display="block" sx={{ mb: 1 }}>Image Preview:</Typography>
              <img 
                src={productFormData.imageBase64} 
                alt="Product Preview" 
                style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'contain' }} 
              />
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenProductDialog(false)} color="secondary">Cancel</Button>
          <Button onClick={handleProductSave} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openProductTypeDialog} onClose={() => setOpenProductTypeDialog(false)}>
        <DialogTitle>Create Product Type</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Product Type Name"
            fullWidth
            value={productTypeFormData.typeName}
            onChange={(e) => setProductTypeFormData({ typeName: e.target.value })}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductTypeDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleProductTypeSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminPanel;
